import { useState, useEffect, useCallback, useRef } from 'react'
import TopBar from './pages/TopBar'
import NotebookSidebar from './pages/Notebooksidebar'
import WorkspacePage from './pages/WorkSpace'
import ExamPage from './pages/ExamPage'
import ProgressPage from './pages/ProgressPage'
import AuthPage from './pages/AuthPage'
import * as api from './api'
import SyllabusPanel from './components/SyllabusPanel'
import YoutubePanel from './components/YoutubePanel'
import { useIsMobile } from './useMediaQuery'
/* ── Toast ─────────────────────────────────────────────────────────────────── */
function Toast ({ msg, icon, color }) {
  return (
    <div className='toast-container'>
      <div className='toast'>
        <span
          className='material-symbols-rounded'
          style={{
            fontSize: 17,
            color: color || 'var(--accent-2)',
            fontVariationSettings: "'FILL' 1"
          }}
        >
          {icon || 'info'}
        </span>
        {msg}
      </div>
    </div>
  )
}

/* ── Upload modal ───────────────────────────────────────────────────────────── */
function UploadModal ({ onClose, onUpload, uploading }) {
  const fileRef = useRef(null)
  const appendModeRef = useRef(false)
  const [dragging, setDragging] = useState(false)
  const [files, setFiles] = useState([])
  const [title, setTitle] = useState('')
  const [mode, setMode] = useState('merge') // "merge" | "separate"

  const addFiles = incoming => {
    const arr = Array.from(incoming || [])
    if (!arr.length) return
    setFiles(prev => {
      const seen = new Set(prev.map(f => f.name + f.size))
      const merged = [...prev, ...arr.filter(f => !seen.has(f.name + f.size))]
      if (!title && merged.length)
        setTitle(merged[0].name.replace(/\.[^.]+$/, ''))
      return merged
    })
  }

  const handleDrop = e => {
    e.preventDefault()
    setDragging(false)
    const incoming = Array.from(e.dataTransfer.files || [])
    if (!incoming.length) return
    addFiles(incoming)
  }

  const handleFile = e => {
    const incoming = Array.from(e.target.files || [])
    if (!incoming.length) return
    if (files.length === 1 && incoming.length === 1 && !appendModeRef.current) {
      setFiles(incoming)
      setTitle(incoming[0].name.replace(/\.[^.]+$/, ''))
    } else {
      addFiles(incoming)
    }
    e.target.value = ''
  }

  const handleChangeClick = e => {
    e.stopPropagation()
    appendModeRef.current = false
    fileRef.current?.click()
  }

  const handleAppendClick = e => {
    e.stopPropagation()
    appendModeRef.current = true
    fileRef.current?.click()
  }

  const removeFile = idx => setFiles(prev => prev.filter((_, i) => i !== idx))
  const handleSubmit = () => {
    if (files.length) onUpload(files, title || files[0].name, mode)
  }

  const totalMB = files.reduce((s, f) => s + f.size, 0) / 1024 / 1024
  const multi = files.length > 1
  const showTitle = !multi || mode === 'merge'

  const modeOptions = [
    {
      key: 'merge',
      icon: 'merge',
      label: 'One notebook',
      desc: 'Combined notes'
    },
    {
      key: 'separate',
      icon: 'content_copy',
      label: 'Separate',
      desc: 'Notes per file'
    }
  ]

  return (
    <div
      className='modal-backdrop'
      onClick={e => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className='modal-box animate-scaleIn'
        style={{ maxWidth: 480, padding: '28px 28px' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 20
          }}
        >
          <h3 style={{ fontSize: 16, fontWeight: 600 }}>
            {files.length > 1 ? 'Import Documents' : 'Import Document'}
          </h3>
          <button onClick={onClose} className='btn btn-ghost btn-icon-sm'>
            <span className='material-symbols-rounded' style={{ fontSize: 18 }}>
              close
            </span>
          </button>
        </div>

        <div
          onDragOver={e => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={e => {
            if (e.target.closest('button') || e.target.closest('input')) {
              return
            }
            appendModeRef.current = false
            fileRef.current?.click()
          }}
          style={{
            border: `2px dashed ${
              dragging
                ? 'var(--accent)'
                : files.length
                ? 'var(--green)'
                : 'var(--border-2)'
            }`,
            borderRadius: 'var(--radius-xl)',
            padding: '28px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            background: dragging
              ? 'var(--accent-soft)'
              : files.length
              ? 'var(--green-soft)'
              : 'var(--surface-2)',
            transition: 'all var(--transition)',
            marginBottom: 16
          }}
        >
          <input
            ref={fileRef}
            type='file'
            multiple
            accept='.pdf,.docx,.pptx,.txt,.png,.jpg,.jpeg'
            style={{ display: 'none' }}
            onChange={handleFile}
          />

          {files.length === 1 ? (
            <>
              <span
                className='material-symbols-rounded'
                style={{
                  fontSize: 32,
                  color: 'var(--green)',
                  fontVariationSettings: "'FILL' 1",
                  display: 'block',
                  marginBottom: 12
                }}
              >
                check_circle
              </span>
              <div
                style={{
                  fontSize: 14.5,
                  fontWeight: 600,
                  color: 'var(--green)',
                  marginBottom: 4,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                }}
              >
                {files[0].name}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--ink-3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8
                }}
              >
                <span>{(files[0].size / 1024 / 1024).toFixed(2)} MB</span>
                <span style={{ color: 'var(--border-2)' }}>·</span>
                <button
                  onClick={handleChangeClick}
                  style={{
                    color: 'var(--accent-2)',
                    textDecoration: 'underline',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 500
                  }}
                >
                  Click to change
                </button>
              </div>
              <div style={{ marginTop: 12 }}>
                <button
                  onClick={handleAppendClick}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    background: 'var(--surface-3)',
                    color: 'var(--ink-2)',
                    border: '1px solid var(--border-2)',
                    borderRadius: 'var(--radius-md)',
                    padding: '6px 12px',
                    fontSize: 11.5,
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all var(--transition)'
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = 'var(--border-2)'
                    e.currentTarget.style.color = 'var(--ink)'
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = 'var(--surface-3)'
                    e.currentTarget.style.color = 'var(--ink-2)'
                  }}
                >
                  <span
                    className='material-symbols-rounded'
                    style={{ fontSize: 14 }}
                  >
                    add
                  </span>
                  Upload another file
                </button>
              </div>
            </>
          ) : files.length > 1 ? (
            <>
              <span
                className='material-symbols-rounded'
                style={{
                  fontSize: 36,
                  color: 'var(--green)',
                  fontVariationSettings: "'FILL' 1",
                  display: 'block',
                  marginBottom: 10
                }}
              >
                library_add_check
              </span>
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 600,
                  color: 'var(--green)',
                  marginBottom: 4
                }}
              >
                {files.length} files selected
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                {totalMB.toFixed(2)} MB total · Click to add more
              </div>
            </>
          ) : (
            <>
              <span
                className='material-symbols-rounded'
                style={{
                  fontSize: 36,
                  color: 'var(--ink-3)',
                  fontVariationSettings: "'FILL' 0",
                  display: 'block',
                  marginBottom: 10
                }}
              >
                upload_file
              </span>
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 500,
                  color: 'var(--ink-2)',
                  marginBottom: 4
                }}
              >
                Drop your files here
              </div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                PDF, DOCX, PPTX, TXT, PNG, JPG · multiple allowed
              </div>
            </>
          )}
        </div>

        {files.length > 1 && (
          <div
            style={{
              marginBottom: 16,
              maxHeight: 150,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 6
            }}
          >
            {files.map((f, i) => (
              <div
                key={f.name + f.size + i}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  background: 'var(--surface-2)',
                  borderRadius: 'var(--radius-md)',
                  padding: '8px 10px'
                }}
              >
                <span
                  className='material-symbols-rounded'
                  style={{
                    fontSize: 18,
                    color: 'var(--sky)',
                    fontVariationSettings: "'FILL' 1"
                  }}
                >
                  description
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12.5,
                      fontWeight: 500,
                      color: 'var(--ink-2)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {f.name}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {(f.size / 1024 / 1024).toFixed(2)} MB
                  </div>
                </div>
                <button
                  onClick={e => {
                    e.stopPropagation()
                    removeFile(i)
                  }}
                  className='btn btn-ghost btn-icon-sm'
                  disabled={uploading}
                >
                  <span
                    className='material-symbols-rounded'
                    style={{ fontSize: 16 }}
                  >
                    close
                  </span>
                </button>
              </div>
            ))}
          </div>
        )}

        {multi && (
          <div style={{ marginBottom: 16 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--ink-3)',
                display: 'block',
                marginBottom: 8
              }}
            >
              How should these be imported?
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {modeOptions.map(opt => {
                const active = mode === opt.key
                return (
                  <button
                    key={opt.key}
                    onClick={() => setMode(opt.key)}
                    disabled={uploading}
                    style={{
                      flex: 1,
                      textAlign: 'left',
                      cursor: 'pointer',
                      border: `1.5px solid ${
                        active ? 'var(--accent)' : 'var(--border-2)'
                      }`,
                      background: active
                        ? 'var(--accent-soft)'
                        : 'var(--surface-2)',
                      borderRadius: 'var(--radius-md)',
                      padding: '10px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      transition: 'all var(--transition)'
                    }}
                  >
                    <span
                      className='material-symbols-rounded'
                      style={{
                        fontSize: 20,
                        color: active ? 'var(--accent)' : 'var(--ink-3)',
                        fontVariationSettings: active ? "'FILL' 1" : "'FILL' 0"
                      }}
                    >
                      {opt.icon}
                    </span>
                    <div>
                      <div
                        style={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: active ? 'var(--accent)' : 'var(--ink-2)'
                        }}
                      >
                        {opt.label}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        {opt.desc}
                      </div>
                    </div>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {showTitle && (
          <div style={{ marginBottom: 20 }}>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--ink-3)',
                display: 'block',
                marginBottom: 6
              }}
            >
              Notebook Title
            </label>
            <input
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder='e.g. Physics Chapter 5–9'
            />
          </div>
        )}

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11.5,
            color: 'var(--ink-3)',
            background: 'var(--surface-2)',
            padding: '8px 12px',
            borderRadius: 'var(--radius-md)',
            marginBottom: 20
          }}
        >
          <span
            className='material-symbols-rounded'
            style={{
              fontSize: 14,
              color: 'var(--sky)',
              fontVariationSettings: "'FILL' 1"
            }}
          >
            info
          </span>
          {multi
            ? mode === 'merge'
              ? 'All files will be merged into one notebook.'
              : 'Each file becomes its own notebook.'
            : 'Large PDFs may take 1–2 minutes to process and index.'}
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} className='btn btn-surface'>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!files.length || uploading}
            className='btn btn-primary'
            style={{
              minWidth: 120,
              opacity: !files.length || uploading ? 0.6 : 1
            }}
          >
            {uploading ? (
              <>
                <div
                  className='spinner spinner-sm'
                  style={{
                    borderTopColor: 'rgba(255,255,255,0.5)',
                    borderColor: 'rgba(255,255,255,0.2)'
                  }}
                />
                Processing…
              </>
            ) : (
              <>
                <span
                  className='material-symbols-rounded'
                  style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}
                >
                  upload
                </span>
                Import
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

/* ── App ────────────────────────────────────────────────────────────────────── */
export default function App () {
  /* Theme */
  const [theme, setTheme] = useState(
    () => localStorage.getItem('sm_theme') || 'dark'
  )
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    localStorage.setItem('sm_theme', theme)
  }, [theme])
  const toggleTheme = () => setTheme(t => (t === 'dark' ? 'light' : 'dark'))

  /* Auth */
  const [user, setUser] = useState(null)
  const [authChecked, setAuthChecked] = useState(false)
  useEffect(() => {
    api
      .getCurrentUser?.()
      .then(u =>
        setUser(
          u
            ? {
                id: u.user_id,
                name: u.name,
                email: u.email,
                avatar: (u.name || u.email || '?')[0].toUpperCase()
              }
            : null
        )
      )
      .catch(() => setUser(null))
      .finally(() => setAuthChecked(true))
  }, [])

  /* Responsive shell */
  const isMobile = useIsMobile()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  // Close the mobile drawer whenever we grow back to desktop.
  useEffect(() => {
    if (!isMobile) setSidebarOpen(false)
  }, [isMobile])

  /* Page */
  const [activePage, setActivePage] = useState('workspace')

  /* Notebooks */
  const [notebooks, setNotebooks] = useState([])
  const [activeId, setActiveId] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [showUpload, setShowUpload] = useState(false)

  useEffect(() => {
    if (!user) {
      setNotebooks([])
      setActiveId(null)
      setMessagesByNb({})
      loadedChatsRef.current = new Set()
      return
    }
    api
      .getNotebooks?.()
      .then(res => {
        const list = res?.items || []
        setNotebooks(list)
        if (list.length) setActiveId(prev => prev || list[0].id)
      })
      .catch(() => setNotebooks([]))
  }, [user])

  const activeNotebook = notebooks.find(n => n.id === activeId) || null

  /* Messages – keyed per notebook, persisted server-side so a notebook keeps a
   * permanent record of every conversation. */
  const [messagesByNb, setMessagesByNb] = useState({})
  const loadedChatsRef = useRef(new Set()) // notebook ids whose history we've fetched
  const messages = messagesByNb[activeId] || []
  const setMessages = useCallback(
    updater => {
      setMessagesByNb(prev => ({
        ...prev,
        [activeId]:
          typeof updater === 'function'
            ? updater(prev[activeId] || [])
            : updater
      }))
    },
    [activeId]
  )

  /* Load the saved conversation the first time a notebook is opened. */
  useEffect(() => {
    if (!user || !activeId) return
    if (loadedChatsRef.current.has(activeId)) return
    loadedChatsRef.current.add(activeId)
    api
      .getChatHistory?.(activeId)
      .then(saved => {
        if (!saved?.length) return
        setMessagesByNb(prev =>
          // Don't clobber a conversation the user has already started this session.
          prev[activeId]?.length ? prev : { ...prev, [activeId]: saved }
        )
      })
      .catch(() => {
        loadedChatsRef.current.delete(activeId) // allow a retry on next open
      })
  }, [user, activeId])

  /* Persist newly-exchanged messages (fire-and-forget). */
  const persistMessages = useCallback((nbId, msgs) => {
    if (!nbId || !msgs?.length) return
    api.saveChatMessages?.(nbId, msgs).catch(() => {})
  }, [])

  /* History of generated study assets, kept per notebook so switching tools
   * (study guide → timeline → …) preserves earlier results instead of
   * overwriting them. Lives here (App never unmounts) so it survives navigation. */
  const [outputsByNb, setOutputsByNb] = useState({})
  const addNotebookOutput = useCallback((nbId, result) => {
    if (!nbId || !result) return
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      ts: Date.now(),
      ...result,
    }
    setOutputsByNb(prev => ({
      ...prev,
      [nbId]: [...(prev[nbId] || []), entry],
    }))
  }, [])

  /* Settings */
  const [tutorMode, setTutorMode] = useState('student')
  const [socraticMode, setSocraticMode] = useState(false)
  const [quizCount, setQuizCount] = useState(8)

  /* Progress Stats */
  const [progressStats, setProgressStats] = useState(null)
  const loadProgressStats = useCallback(() => {
    if (!user) return
    api
      .getProgressStats?.()
      .then(stats => setProgressStats(stats))
      .catch(() => {})
  }, [user])

  useEffect(() => {
    if (activePage === 'progress') {
      loadProgressStats()
    }
  }, [activePage, loadProgressStats])

  /* Toast */
  const [toast, setToast] = useState(null)
  const showToast = (msg, icon, color, duration = 2800) => {
    setToast({ msg, icon, color })
    setTimeout(() => setToast(null), duration)
  }

  /* Global processing indicator */
  const [processing, setProcessing] = useState(false)
  const [processingLabel, setProcessingLabel] = useState('')
  const handleProcessingChange = useCallback((on, label = '') => {
    setProcessing(on)
    setProcessingLabel(label)
  }, [])

  /* Upload */
  const handleUpload = async (files, title, mode = 'merge') => {
    const list = Array.isArray(files) ? files : files ? [files] : []
    if (list.length === 0) {
      setShowUpload(true)
      return
    }
    setUploading(true)
    setShowUpload(false)

    const label =
      list.length > 1
        ? mode === 'separate'
          ? `Importing ${list.length} files…`
          : `Merging ${list.length} files…`
        : 'Importing document…'
    handleProcessingChange(true, label)

    try {
      const { notebooks: createdNotebooks, warnings } =
        await api.uploadNotebook(list, title, mode)
      if (!createdNotebooks.length)
        throw new Error('No notebooks were created.')

      setNotebooks(prev => [...createdNotebooks, ...prev])
      setActiveId(createdNotebooks[0].id)

      showToast(
        createdNotebooks.length > 1
          ? `${createdNotebooks.length} notebooks imported`
          : `"${createdNotebooks[0].title}" imported successfully`,
        'check_circle',
        'var(--green)'
      )
      if (warnings?.length) {
        showToast(
          `Some files were skipped: ${warnings[0]}`,
          'warning',
          'var(--rose)',
          6000
        )
      }
    } catch (e) {
      showToast(
        e.message || 'Upload failed. Please try again.',
        'error',
        'var(--rose)',
        6000
      )
    } finally {
      setUploading(false)
      handleProcessingChange(false)
    }
  }

  /* Rename notebook */
  const handleRename = async (id, title) => {
    const clean = (title || '').trim().slice(0, 80)
    if (!clean) return
    // Optimistic update
    setNotebooks(prev =>
      prev.map(n => (n.id === id ? { ...n, title: clean } : n))
    )
    try {
      await api.renameNotebook?.(id, clean)
      showToast('Notebook renamed', 'edit', 'var(--accent-2)')
    } catch (e) {
      showToast(
        e.message || 'Rename failed. Please try again.',
        'error',
        'var(--rose)'
      )
      // Reload authoritative list on failure
      api
        .getNotebooks?.()
        .then(res => setNotebooks(res?.items || []))
        .catch(() => {})
    }
  }

  /* Delete notebook */
  const handleDelete = async id => {
    try {
      await api.deleteNotebook?.(id)
      setNotebooks(prev => prev.filter(n => n.id !== id))
      setMessagesByNb(prev => {
        const { [id]: _drop, ...rest } = prev
        return rest
      })
      loadedChatsRef.current.delete(id)
      if (activeId === id)
        setActiveId(notebooks.find(n => n.id !== id)?.id || null)
      showToast('Notebook deleted', 'delete', 'var(--rose)')
    } catch {
      showToast('Could not delete notebook.', 'error', 'var(--rose)')
    }
  }

  /* Logout */
  const handleLogout = async () => {
    await api.logout?.()
    setUser(null)
  }

  if (!authChecked) {
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--bg)',
          flexDirection: 'column',
          gap: 14
        }}
      >
        <div style={{ position: 'relative', width: 52, height: 52 }}>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              border: '3px solid var(--border-2)',
              borderTopColor: 'var(--accent)',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <span
              className='material-symbols-rounded'
              style={{
                fontSize: 22,
                color: 'var(--accent-2)',
                fontVariationSettings: "'FILL' 1"
              }}
            >
              psychology
            </span>
          </div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          Loading StudyMind…
        </div>
      </div>
    )
  }

  if (!user) {
    return <AuthPage onSignIn={setUser} />
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateRows: 'var(--header-h) 1fr',
        gridTemplateColumns: '1fr',
        width: '100vw',
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--bg)'
      }}
    >
      {/* Top bar */}
      <TopBar
        theme={theme}
        toggleTheme={toggleTheme}
        isMobile={isMobile}
        onMenuClick={() => setSidebarOpen(o => !o)}
        activePage={activePage}
        setActivePage={page => {
          setActivePage(page)
          // Auto-select first notebook when switching to exam if none selected
          if (page === 'exam' && !activeId && notebooks.length) {
            setActiveId(notebooks[0].id)
          }
        }}
        isProcessing={processing}
        processingLabel={processingLabel}
        user={user}
        onLogout={handleLogout}
        onSearch={() =>
          showToast('Search coming soon!', 'search', 'var(--accent-2)')
        }
      />

      {/* Main content */}
      <div style={{ display: 'flex', overflow: 'hidden', position: 'relative', minHeight: 0 }}>
        {/* Sidebar — inline on desktop, off-canvas drawer on mobile */}
        {(!isMobile || sidebarOpen) && (
          <>
            {isMobile && (
              <div
                className='drawer-backdrop'
                onClick={() => setSidebarOpen(false)}
              />
            )}
            <div className={isMobile ? 'mobile-drawer' : 'sidebar-shell'}>
              <NotebookSidebar
                notebooks={notebooks}
                activeId={activeId}
                userId={user?.id}
                isMobile={isMobile}
                onClose={() => setSidebarOpen(false)}
                setActiveId={id => {
                  setActiveId(id)
                  setActivePage('workspace')
                  if (isMobile) setSidebarOpen(false)
                }}
                uploading={uploading}
                onUpload={() => setShowUpload(true)}
                onDelete={handleDelete}
                onRename={handleRename}
              />
            </div>
          </>
        )}

        {/* Page views */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            overflow: 'hidden',
            minWidth: 0
          }}
        >
          {activePage === 'workspace' && (
            <WorkspacePage
              notebooks={notebooks}
              activeId={activeId}
              activeNotebook={activeNotebook}
              isMobile={isMobile}
              messages={messages}
              setMessages={setMessages}
              onPersistMessages={persistMessages}
              onUpload={() => setShowUpload(true)}
              uploading={uploading}
              outputs={outputsByNb[activeId] || []}
              onAddOutput={addNotebookOutput}
              onProcessingChange={handleProcessingChange}
              tutorMode={tutorMode}
              setTutorMode={setTutorMode}
              socraticMode={socraticMode}
              setSocraticMode={setSocraticMode}
              quizCount={quizCount}
              setQuizCount={setQuizCount}
            />
          )}

          {activePage === 'syllabus' && <SyllabusPanel />}

          {/* ── NEW: YouTube Notes page ── */}
          {activePage === 'youtube' && <YoutubePanel />}

          {activePage === 'exam' && (
            <ExamPage
              notebooks={notebooks}
              activeNotebook={activeNotebook}
              onGoToChat={() => setActivePage('workspace')}
              onProcessingChange={handleProcessingChange}
            />
          )}

          {activePage === 'progress' && (
            <ProgressPage
              notebooks={notebooks}
              progressStats={progressStats}
              onGoExam={() => setActivePage('exam')}
            />
          )}
        </div>
      </div>

      {/* Upload modal */}
      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onUpload={handleUpload}
          uploading={uploading}
        />
      )}

      {/* Toast */}
      {toast && <Toast msg={toast.msg} icon={toast.icon} color={toast.color} />}
    </div>
  )
}
